import { defaultRecipe } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { GroupLabel, Panel } from '../controls/Panel.tsx';
import { Slider } from '../controls/Slider.tsx';
import { Tip } from '../controls/Tooltip.tsx';

const DEFAULTS = defaultRecipe();

export function DetailPanel() {
  const photo = useSelectedPhoto();
  const updateRecipe = useStore((state) => state.updateRecipe);
  const commit = useStore((state) => state.commit);

  if (!photo) return null;
  const recipe = photo.recipe;

  const active =
    recipe.sharpenAmount !== DEFAULTS.sharpenAmount ||
    recipe.sharpenRadius !== DEFAULTS.sharpenRadius ||
    recipe.sharpenDetail !== DEFAULTS.sharpenDetail ||
    recipe.sharpenMasking !== DEFAULTS.sharpenMasking ||
    recipe.noiseLuminance !== DEFAULTS.noiseLuminance ||
    recipe.noiseLuminanceDetail !== DEFAULTS.noiseLuminanceDetail ||
    recipe.noiseColor !== DEFAULTS.noiseColor;

  return (
    <Panel id="detail" title="Detail" active={active}>
      <GroupLabel>
        <Tip
          title="Sharpening"
          body="Only readable at 1:1. Press 1 for a full-size view before you move anything here."
        >
          Sharpening
        </Tip>
      </GroupLabel>
      <Slider
        label="Amount"
        value={recipe.sharpenAmount}
        min={0}
        max={150}
        step={1}
        neutral={DEFAULTS.sharpenAmount}
        help="Edge crispness. Raise it until edges look sharp and stop before they gain a bright outline."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.sharpenAmount = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Radius"
        value={recipe.sharpenRadius}
        min={0.5}
        max={3}
        step={0.1}
        neutral={DEFAULTS.sharpenRadius}
        unit=" px"
        help="Size of the edges being sharpened. Around 0.8 for portraits, 1.5 and up for soft lenses."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.sharpenRadius = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Detail"
        value={recipe.sharpenDetail}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.sharpenDetail}
        help="How much fine texture is included. High values sharpen noise too, so save them for clean files."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.sharpenDetail = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Masking"
        value={recipe.sharpenMasking}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.sharpenMasking}
        help="Keeps sharpening on edges so skin and sky stay smooth. On faces, 40 to 70."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.sharpenMasking = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>
        <Tip
          title="Noise reduction"
          body="Grain hides in a fit-to-window view. Press 1 before you decide how much you need."
        >
          Noise reduction
        </Tip>
      </GroupLabel>
      <Slider
        label="Luminance"
        value={recipe.noiseLuminance}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.noiseLuminance}
        help="Smooths grain. Past about 40 it starts eating hair, fabric and foliage with it."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.noiseLuminance = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Detail"
        value={recipe.noiseLuminanceDetail}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.noiseLuminanceDetail}
        help="How hard the smoothing protects edges. Higher keeps texture, lower goes cleaner and waxier."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.noiseLuminanceDetail = value;
          })
        }
        onCommit={commit}
      />
      <Slider
        label="Colour"
        value={recipe.noiseColor}
        min={0}
        max={100}
        step={1}
        neutral={DEFAULTS.noiseColor}
        help="Clears the coloured blotches high-ISO shadows pick up. Costs almost no real detail."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.noiseColor = value;
          })
        }
        onCommit={commit}
      />
    </Panel>
  );
}
